
import React, { useEffect, useState, useMemo } from 'react';
import { useComplianceStore } from '../../../store/complianceStore';
import ComplianceCertificateForm from './ComplianceCertificateForm';
import { ComplianceCertificate } from '../../../types/api';
import ViewCertificate from './ViewCertificate';
import Modal from '../../../components/modal/Modal';
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../../styles/ag-grid-custom.css";
import images from "../../../assets/images";
import { TrashIcon, RotateCw } from "lucide-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { SpinLoader } from "../../../components/loader/Loader";

const CertificateList: React.FC = () => {
  const { certificates, fetchCertificates, loading, error, deleteCertificate } = useComplianceStore();
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<ComplianceCertificate | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [renewData, setRenewData] = useState<{ id?: string; assetId: string; assetName: string; certificateType: string; } | null>(null);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  const handleView = (cert: ComplianceCertificate) => {
    setSelectedCertificate(cert);
    setIsViewModalOpen(true);
  };
  
  const handleDelete = async (id: string) => {
    await deleteCertificate(id);
  }

  const handleRenew = (cert: ComplianceCertificate) => {
      setRenewData({
          id: cert.id,
          assetId: cert.assetId,
          assetName: cert.assetName,
          certificateType: cert.certificateType
      });
      setIsFormModalOpen(true);
  };

  const statusCellRenderer = (params: ICellRendererParams) => {
    const status = params.value;
    let statusClass = "bg-gray-100 text-gray-800";
    
    switch (status) {
      case 'Valid':
        statusClass = "bg-green-100 text-green-800";
        break;
      case 'Expiring Soon':
        statusClass = "bg-yellow-100 text-yellow-800";
        break;
      case 'Expired':
        statusClass = "bg-red-100 text-red-800";
        break;
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusClass}`}>
        {status}
      </span>
    );
  };

  const actionCellRenderer = (params: ICellRendererParams) => {
      return (
        <div className="flex gap-2 mt-2 items-center">
            <button
              className="focus:outline-none"
              onClick={() => handleView(params.data)}
              title="View"
            >
              <img className="iconTd" src={images.viewIcon} alt="View" />
            </button>
            
             <button
                className="focus:outline-none text-blue-600 hover:text-blue-800"
                onClick={() => handleRenew(params.data)}
                title="Renew"
             >
                <RotateCw className="w-5 h-5" />
             </button>

             <button
                className="focus:outline-none"
                onClick={() => handleDelete(params.data.id)}
                title="Delete"
             >
                <TrashIcon className="text-red-500 w-5 h-5" />
             </button>
        </div>
      );
  };

  const columnDefs: ColDef[] = useMemo(() => [
    {
        headerName: "Asset Name",
        field: "assetName",
        flex: 1,
        cellStyle: { padding: "10px 10px 0 10px" },
        cellRenderer: (params: ICellRendererParams) => (
            <div>
                <div className="text-sm font-medium text-gray-900">{params.data.assetName}</div>
                <div className="text-xs text-gray-500">{params.data.assetId}</div>
            </div>
        )
    },
    {
        headerName: "Certificate Type",
        field: "certificateType",
        flex: 1,
        cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
        headerName: "Issue Date",
        field: "issueDate",
        flex: 1,
        cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
        headerName: "Expiry Date",
        field: "expiryDate",
        flex: 1,
        cellStyle: { padding: "10px 10px 0 10px" },
    },
    {
        headerName: "Status",
        field: "status",
        flex: 1,
        cellStyle: { padding: "10px 10px 0 10px" },
        cellRenderer: statusCellRenderer,
    },
    {
        headerName: "Actions",
        field: "actions",
        flex: 1,
        cellStyle: { padding: "10px 10px 0 10px" },
        cellRenderer: actionCellRenderer,
    }
  ], []);

  return (
    <div className="flex flex-col h-full space-y-4">
        <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
            <h2 className="text-lg font-semibold text-gray-700">Regulatory Certificates</h2>
            <button 
                onClick={() => {
                    setRenewData(null);
                    setIsFormModalOpen(true);
                }}
                className="bg-[#0fa9a6] text-white px-5 py-2 rounded-full font-semibold hover:bg-[#197f7d] transition-all flex items-center"
            >
                <div className="flex items-center gap-2">
                    <img src={images.addPremiessIcon} alt="" width={25} />
                     UPLOAD CERTIFICATE
                </div>
            </button>
        </div>

      {/* Grid Container */}
      <div 
        className="bg-white rounded-md shadow-sm p-4 ag-theme-alpine"
        style={{ height: "calc(100vh - 250px)", width: "100%" }}
      >
          {loading ? (
             <div className="flex justify-center items-center h-full text-gray-500">
               <SpinLoader />
             </div>
          ) : error ? (
             <div className="flex justify-center items-center h-full text-red-500">Error: {error}</div>
          ) : (
            <AgGridReact
                rowData={certificates}
                columnDefs={columnDefs}
                rowHeight={60}
                headerHeight={60}
                onGridReady={(params) => params.api.sizeColumnsToFit()}
                suppressCellFocus={true}
                animateRows={true}
            />
          )}
      </div>

      {/* Upload Form Modal */}
      <Modal
  isOpen={isFormModalOpen}
  closeButton={false}
  modalClass="bg-white w-full max-w-3xl max-h-[90vh] rounded-xl shadow-lg flex flex-col p-0 overflow-hidden"
  onClose={() => setIsFormModalOpen(false)}
>
  <ComplianceCertificateForm 
    onSuccess={() => setIsFormModalOpen(false)} 
    onCancel={() => setIsFormModalOpen(false)} 
    initialData={renewData}
  />
</Modal>

      {/* View Certificate Modal */}
<Modal
  isOpen={isViewModalOpen}
  closeButton={false}
  modalClass="bg-white w-full max-w-3xl max-h-[90vh] rounded-xl shadow-lg flex flex-col p-0 overflow-hidden"
  onClose={() => setIsViewModalOpen(false)}
>
  {selectedCertificate && (
    <ViewCertificate
      certificate={selectedCertificate}
      onClose={() => setIsViewModalOpen(false)}
    />
  )}
</Modal>
    </div>
  );
};

export default CertificateList;

